<script setup>
    import { Swiper, SwiperSlide } from 'swiper/vue';
    import { EffectFade, Navigation, Pagination, A11y, Thumbs, FreeMode } from 'swiper';
    import 'swiper/css';
    import 'swiper/css/navigation';
    import 'swiper/css/pagination';
    import 'swiper/css/effect-fade';
    import { onMounted, ref } from "vue";

    const props = defineProps({caseStudies: Object})
    const modules = [EffectFade, Navigation, Pagination, A11y, Thumbs, FreeMode]

    const thumbsSwiper = ref(null),
          activeIndex = ref(0),
          speed = ref(300)

    onMounted(() => {
        if (window?.reduced_motion) {
            speed.value = 0;
        }
    })

    function onSwiper(swiper) {
        // console.log(swiper)
    }

    function onSlideChange(swiper) {
        activeIndex.value = swiper.activeIndex

        if (window.innerWidth < 1200) {
            setTimeout(() => {
                swiper.snapGrid = [...swiper.slidesGrid]
                thumbsSwiper.value.slideTo(swiper.activeIndex)
            }, 300)
        }
    }

    const setThumbsSwiper = (swiper) => {
        thumbsSwiper.value = swiper
    }
</script>
<template>
    <div class="block-inner">
        <div class="grid w-full">
            <div class="thumbs-slider flex items-end w-full overflow-hidden">
                <swiper
                    :modules="modules"
                    :slides-per-view="2"
                    :loop="false"
                    :speed="speed"
                    :breakpoints="{
                        768: {
                            slidesPerView: props.caseStudies.length > 3 ? 3 : props.caseStudies.length
                        },
                        1200: {
                            slidesPerView: props.caseStudies.length > 6 ? 6 : props.caseStudies.length
                        }
                    }"
                    :watchSlidesProgress="true"
                    class="w-[calc(100%_-_theme(space.24))] border-b border-cintra-grey"
                    @slideChange="onSlideChange"
                    @swiper="setThumbsSwiper"
                >
                    <swiper-slide
                        v-for="(caseStudy, index) in props.caseStudies"
                        class="case-study-thumb"
                    >
                        <div class="cursor-pointer h-32 flex flex-col justify-center items-center p-6" :key="index">
                            <img
                                v-if="caseStudy.logo"
                                class="w-full h-full object-contain"
                                :src="caseStudy.logo.permalink"
                                :alt="caseStudy.logo?.alt"
                            >
                            <span v-else>
                                {{ caseStudy.title }}
                            </span>
                        </div>
                    </swiper-slide>
                </swiper>
                <div class="text-4xl w-24 h-12 flex items-center justify-between">
                    <button class="cintra-case-study-slider-prev w-12 h-12 relative transition duration-300 hover:text-cintra-orange">
                        <i class="fa-light fa-circle-arrow-left absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2"></i>
                    </button>
                    <button class="cintra-case-study-slider-next w-12 h-12 relative transition duration-300 hover:text-cintra-orange">
                        <i class="fa-light fa-circle-arrow-right absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2"></i>
                    </button>
                </div>
            </div>
            <swiper
                :modules="modules"
                :slides-per-view="1"
                :loop="false"
                :speed="speed"
                :navigation="{
                    nextEl: '.cintra-case-study-slider-next',
                    prevEl: '.cintra-case-study-slider-prev'
                }"
                :effect="'fade'"
                :fadeEffect="{ crossFade: true }"
                :thumbs="{ swiper: thumbsSwiper }"
                class="w-full"
                @swiper="onSwiper"
                @slideChange="onSlideChange"
            >
                <swiper-slide v-for="(caseStudy, index) in props.caseStudies">
                    <div class="grid md:grid-cols-5 lg:grid-cols-3 gap-14 md:gap-20 lg:gap-28 py-10" :key="index">
                        <div class="w-full md:col-span-2 lg:col-span-1 pb-14 md:py-14">
                            <div class="aspect-4/3 w-full relative rounded-3xl relative z-[1]">
                                <div class="h-full w-full overflow-hidden relative">
                                    <img
                                        v-if="caseStudy.featured_image"
                                        :src="caseStudy.featured_image.permalink"
                                        class="w-full h-full absolute top-1/2 left-1/2 -translate-x-1/2 object-cover transition duration-300 delay-300 rounded-3xl"
                                        :class="{
                                            'opacity-100 motion-safe:-translate-y-1/2' : activeIndex === index,
                                            'opacity-0 translate-y-0' : activeIndex !== index
                                        }"
                                        :alt="caseStudy.featured_image?.alt"
                                    >
                                </div>
                                <div class="p-4 h-28 absolute top-full left-0 right-0 -translate-y-1/2 -z-[1]" aria-hidden="true">
                                    <div class="h-full w-full bg-cintra-orange opacity-30 rounded-3xl blur-lg" />
                                </div>
                            </div>
                        </div>
                        <div class="md:col-span-3 lg:col-span-2 text w-full text-cintra-grey flex flex-col justify-center md:pr-24">
                            <div>
                                <h3
                                    class="text-5xl m-auto transition duration-300 delay-300"
                                    :class="{
                                        'opacity-100 translate-y-0' : activeIndex === index,
                                        'opacity-0 motion-safe:translate-y-full' : activeIndex !== index
                                    }"
                                >
                                    {{ caseStudy.title }}
                                </h3>
                                <p
                                    class="py-8 m-0 transition duration-300 delay-500"
                                    :class="{
                                        'opacity-100 translate-y-0' : activeIndex === index,
                                        'opacity-0 motion-safe:translate-y-full' : activeIndex !== index
                                    }"
                                >
                                    {{ caseStudy.description }}
                                </p>
                                <div
                                    class="transition duration-300 delay-700"
                                    :class="{
                                        'opacity-100 translate-y-0' : activeIndex === index,
                                        'opacity-0 motion-safe:translate-y-full' : activeIndex !== index
                                    }"
                                >
                                    <a
                                        class="arrow-button"
                                        :href="caseStudy.permalink"
                                    >
                                        view case study
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </swiper-slide>
            </swiper>
        </div>
    </div>
</template>

<style lang="scss">
    .thumbs-slider .case-study-thumb {
        @apply transition;
        @apply duration-300;
        @apply border-b-4;
        @apply border-b-transparent;

        @media screen and (max-width: 1199px) {
            &.swiper-slide-active.swiper-slide-visible {
                @apply border-b-cintra-orange;
            }
        }
    }
    .swiper-button-disabled {
        @apply pointer-events-none;
        @apply opacity-30;
    }

    @media screen and (min-width: 1200px) {
        .thumbs-slider {
            .case-study-thumb:not(.swiper-slide-thumb-active) {
                @apply border-b-transparent;
            }
            .case-study-thumb.swiper-slide-thumb-active {
                @apply border-b-cintra-orange;
            }
        }
    }
</style>
